/* eslint-disable no-console */
import Configuration from "../../../Configuration";

export type ApiServiceType = "auth" | "site" | "res" | "file";

/**
 * Debug message received from the backend
 */
interface Message {
    traceId: string;
    level: number;
    key: string;
    message: string;
}

/**
 * The default response object from every microservice.
 * Messages can contain data to be able to debug why something went wrong
 * (Use the traceID in combination with Seq)
 */
interface Response<T> {
    continuationToken?: string | undefined;
    resultObject: T;
    messages: Message[];
}

/**
 * This class is contains most of the generic API requests we can make
 * with our backend service.
 * Its mostly used as a namespace, and should not be instantiated.
 */
export default class Agent {
    /**
     * Builds the correct URL instance from the given endpoint and serviceType
     * The serviceType generally indicates what microservice to communicate with
     * @param {string} endpoint             The endpoint of the api service
     * @param {ApiServiceType} serviceType  The ApiService to build an URL for
     */
    static buildURL(endpoint: string, serviceType: ApiServiceType): URL {
        const finalEndpoint = `api/${endpoint}`;
        let apiURL = null;
        switch (serviceType) {
            case "auth":
                apiURL = Configuration.get("authApiURL");
                break;
            case "site":
                apiURL = Configuration.get("siteApiURL");
                break;
            case "file":
                apiURL = Configuration.get("fileApiURL");
                break;
            default:
                console.error(`Unknown api service type: ${serviceType}`);
                apiURL = Configuration.get("siteApiURL");
        }
        return new URL(finalEndpoint, apiURL);
    }

    /**
     * Executes a given request and returns the resonesObject of type `Response<T>`
     * This function will attempt to convert the body of the request into something
     * that also works with Safari.
     * @param {Request} request        The request to execute
     */
    static async execute<T>(
        request: Request,
    ): Promise<Response<T | undefined>> {
        const response = await fetch(request);
        if (response.status === 401) {
            // Unauthorized: clear the session and reload
            localStorage.clear();
            window.location.reload();
        }
        if (response.status < 200 || response.status >= 300) {
            const result: Response<undefined> = await response.json();
            return result;
        }
        const responseObject: Response<T> = await response.json();
        if (responseObject.messages && responseObject.messages.length > 0) {
            console.group("api_error");
            console.log("API URL executed: ", request.url);
            console.log("Request: ", request);
            console.log("Msgs from backend: ", responseObject.messages);
            console.groupEnd();
        }
        return responseObject;
    }

    /**
     * Execute a get request on the given endpoint. Default service type is 'auth'.
     * @param {string} endpoint             The endpoint to get a resource from
     * @param {Headers} headers             Any custom HTTP headers that are needed (optional)
     * @param {ApiServiceType} serviceType  The specific microservice to communicate with
     */
    static async get<T>(
        endpoint: string,
        headers: Headers = new Headers(),
        serviceType: ApiServiceType = "auth",
    ): Promise<Response<T | undefined>> {
        const url = Agent.buildURL(endpoint, serviceType);
        const request = new Request(url.href, {
            method: "GET",
            headers,
        });
        return Agent.execute(request);
    }

    static async post<T>(
        endpoint: string,
        body: Record<string, unknown> | ArrayBuffer,
        headers: Headers = new Headers(),
        serviceType: ApiServiceType = "auth",
    ): Promise<Response<T | undefined>> {
        const url = Agent.buildURL(endpoint, serviceType);

        // For now we are only sending JSON bodies; the server demands this header
        if (!headers.has("Content-Type")) {
            headers.append("Content-Type", "application/json");
            const request = new Request(url.href, {
                method: "POST",
                headers,
                body: JSON.stringify(body),
            });
            return Agent.execute(request);
        }

        const request = new Request(url.href, {
            method: "POST",
            headers,
            body: body as ArrayBuffer,
        });
        return Agent.execute(request);
    }

    static async put<T>(
        endpoint: string,
        body: Record<string, unknown>,
        headers: Headers = new Headers(),
        serviceType: ApiServiceType = "auth",
    ): Promise<Response<T | undefined>> {
        const url = Agent.buildURL(endpoint, serviceType);
        headers.append("content-type", "application/json");
        const request = new Request(url.href, {
            method: "PUT",
            headers,
            body: JSON.stringify(body),
        });
        return Agent.execute(request);
    }
}
